import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Share, X } from "lucide-react";

import { useIsMobile, useIsStandalone } from "@/hooks/useMediaQuery";
import { haptic } from "@/lib/haptics";
import { BrandMark } from "@/components/mobile/AppSplash";

const DISMISS_KEY = "flowgantt_install_dismissed_at";
const SNOOZE_MS = 30 * 24 * 60 * 60 * 1000; // ask again after 30 days

const isIOS = () =>
  typeof navigator !== "undefined" &&
  (/iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));

const recentlyDismissed = () => {
  try {
    const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return at && Date.now() - at < SNOOZE_MS;
  } catch {
    return false;
  }
};

/**
 * "Install the app" card shown on phones when FlowGantt runs in a browser tab.
 *  - Android / Chromium: uses the native beforeinstallprompt flow (one tap).
 *  - iOS Safari: there is no install API, so it explains Share → Add to Home Screen.
 * Hidden once installed, and snoozed for 30 days after being dismissed.
 */
export default function InstallPrompt() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const standalone = useIsStandalone();
  const [deferred, setDeferred] = useState(null);
  const [dismissed, setDismissed] = useState(recentlyDismissed);
  const ios = isIOS();

  useEffect(() => {
    const onBefore = (e) => {
      e.preventDefault(); // keep the event so we can fire it from our own button
      setDeferred(e);
    };
    const onInstalled = () => setDeferred(null);
    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (!isMobile || standalone || dismissed) return null;
  if (!deferred && !ios) return null; // nothing actionable on this browser

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* private mode: the card will simply come back next visit */
    }
    setDismissed(true);
  };

  const install = async () => {
    haptic("light");
    if (!deferred) return;
    deferred.prompt();
    try {
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") setDeferred(null);
      else dismiss();
    } catch {
      dismiss();
    }
  };

  return (
    <div
      data-testid="install-prompt"
      className="relative mb-5 flex items-center gap-3 rounded-2xl border border-stone-200 bg-stone-50 p-3 pr-10"
    >
      <BrandMark className="h-11 w-11 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="font-display text-[15px] font-black tracking-tight">
          {t("mobile.install.title", "Installer FlowGantt")}
        </div>
        {deferred ? (
          <div className="mt-0.5 text-xs leading-snug text-neutral-500">
            {t("mobile.install.subtitle", "Plein écran, accès direct depuis l'écran d'accueil.")}
          </div>
        ) : (
          <div className="mt-0.5 text-xs leading-snug text-neutral-500">
            {t("mobile.install.iosPre", "Touchez")}{" "}
            <Share className="inline h-3.5 w-3.5 -translate-y-px text-[#E05236]" strokeWidth={2} />{" "}
            {t("mobile.install.iosPost", "puis « Sur l'écran d'accueil »")}
          </div>
        )}
      </div>
      {deferred && (
        <button
          type="button"
          data-testid="install-prompt-cta"
          onClick={install}
          className="shrink-0 rounded-full bg-neutral-950 px-4 py-2 text-xs font-semibold text-white active:bg-neutral-800"
        >
          {t("mobile.install.cta", "Installer")}
        </button>
      )}
      <button
        type="button"
        data-testid="install-prompt-close"
        aria-label={t("common.close")}
        onClick={dismiss}
        className="absolute right-1.5 top-1.5 grid h-8 w-8 place-items-center rounded-full text-neutral-400 active:bg-stone-200"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
